/**
 * Route metadata for the shell. The route → screen-component wiring is added as
 * screens are ported (see screens/registry); this file holds the stable routing
 * data: the canonical route id list and the read-level capability guards.
 *
 * Ported verbatim from the prototype's ROUTE_TABLE keys + ROUTE_READ_PERM.
 */

/** Every navigable route id in the app (the ROUTE_TABLE keys). */
export const ROUTE_IDS = [
  "signin", "dashboard", "eventday", "events", "events.hub", "templates", "templates.detail",
  "ticket-groups", "ticket-groups.edit", "ticket-types", "ticket-types.edit",
  "questions", "questions.groups", "questions.edit", "questions.group.edit",
  "promos", "promos.edit",
  "services", "services.edit", "services.categories", "services.category.edit", "services.packages", "services.package.edit",
  "products", "products.edit",
  "people",
  "staff", "staff.edit", "staff.groups", "staff.group.edit",
  "resources", "resources.edit", "resources.groups", "resources.group.edit",
  "queues", "appointments", "appointment.manage", "appointment.detail",
  "queues.console", "queues.flows", "queues.flow", "queues.health", "queues.dashboard", "queues.boards", "queues.archive", "queues.create", "queue.join", "queue.display",
  "messaging", "messaging.flows", "messaging.flow", "messaging.delivery", "messaging.compose",
  // Full-screen email WYSIWYG for a library message (2026-07-30, Greg — messaging
  // overhaul step 2). Unguarded like messaging.compose; the screen itself
  // read-onlys without campaigns.write.
  "messaging.compose.email",
  "messaging.appointments",
  "guests.responses",
  "calendar.week", "calendar.month", "calendar.day",
  "create.type", "create.ai", "create.basics", "create.details", "create.dates", "create.tickets",
  "create.registration", "create.landing", "create.messaging", "create.guests", "create.approvals", "create.live", "create.publish",
  "hub.create.basics", "hub.create.page", "hub.create.publish", "hub.create.live",
  "guests", "guests.profile", "guests.import", "guests.import.map",
  "segments", "segments.detail", "segments.new", "waitlist", "invite",
  "campaigns", "campaigns.email", "campaigns.sms", "campaigns.notifications",
  "reports", "reports.event", "reports.library", "reports.detail",
  "settings.locations", "locations", "locations.edit", "locations.import", "settings.closures", "settings.import", "settings.import.status",
  "settings.integ", "settings.branding", "branding", "settings.calendar", "settings.approvals", "settings.ai",
  "approvals.process.edit", "approvals", "approvals.review", "settings.billing", "settings.tax", "orders", "account.details",
  "notifications", "checkins.today", "registrations.today", "vip.confirmations", "inbox",
  "landing.pages", "landing.editor", "hub.pages", "hub.editor",
  "hub.public.location", "hub.public.group", "hub.public.workspace", "event.public", "event.register",
  "events.archived", "guests.imports",
  "campaigns.sent", "campaigns.scheduled", "campaigns.drafts", "campaigns.approvals",
  "reports.guests", "reports.campaigns", "reports.revenue", "reports.appointments",
  "settings.general", "settings.security", "settings.audit", "settings.api", "settings.integ.detail",
  "help", "help.article",
  "surveys", "surveys.questions", "surveys.edit", "surveys.question.edit",
  "workforce", "workforce.schedule", "workforce.forecast", "workforce.coverage",
  "workforce.staffing", "workforce.staffing.event", "workforce.ops", "workforce.requests",
  "workforce.rules", "workforce.myday",
  // Working copy of My Day (2026-07-30) — same read guard as the original, below.
  "workforce.myday.sandbox",
  // Its day/week/month schedule (2026-07-31) — same read guard again.
  "workforce.myschedule.sandbox",
  // Merged "My Schedule & Time" (2026-07-31) — same read guard again.
  "workforce.scheduletime.sandbox",
  // My Skills & Services (2026-08-01) — same self-scope read guard.
  "workforce.myskills",
  "storeops", "storeops.floor", "storeops.insights", "storeops.clients", "storeops.events",
  "storeops.roster", "storeops.skills", "storeops.perf", "storeops.loyalty", "storeops.targets",
  "storeops.schedules", "storeops.staff", "storeops.integrations", "storeops.compliance",
  "storeops.settings", "storeops.region",
  "storeops.my.day", "storeops.my.book", "storeops.my.targets", "storeops.my.schedule",
  // Aperture Bank six-persona port (P1, 2026-07-17) — pending-port surfaces
  "workforce.setup", "workforce.copilot", "workforce.runs", "workforce.draft",
  "workforce.marketplace", "workforce.reporting", "workforce.payroll", "workforce.advisors",
  "workforce.compliance", "workforce.integration", "workforce.planning", "workforce.coaching",
  "workforce.assists", "workforce.requisitions",
  "workforce.region", "workforce.region.compare", "workforce.region.redistribute",
  "workforce.region.runs", "workforce.region.scope",
  "workforce.floor",
  /* Calendar insertion phase 2 (2026-08-08, Greg — docs/floor-calendar-port.md):
     the Floor Calendar day board. Own top-level section id, so neither the
     Workforce item nor a future Floor item double-lights (rail longest-match). */
  "floor.calendar",
  /* Calendar insertion, region layer (2026-08-10 — docs/floor-calendar-port.md):
     the Regional Calendar. Sibling section id to floor.calendar for the same
     rail longest-match reason. */
  "floor.region",
  "workforce.myperf", "workforce.myrelationships",
  "ops.today", "ops.walkins", "ops.services",
  /* REQ-128 (2026-08-03): the family-tree catalogue. `ops.catalogue` is Riley's configurable
     view; `workforce.services.view` is the SAME screen read-only for the branch manager and
     the advisor — gated on myschedule.read, deliberately NOT services.read, because an
     advisor has no catalogue-read capability and the point is that he can still see what
     his branch offers. */
  "ops.catalogue", "workforce.services.view", "workforce.coverage.staff",
  "journeys",
  // Customers port (2026-07-24) — the "book of everyone who engages" screen,
  // visible to every persona across all three tenants. No read guard by design.
  "customers",
] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

/**
 * Minimum capability to LOAD a route — enforced centrally so a role that can't
 * see a surface can't deep-link into it. Action-level gates (write/publish/
 * delete/export) still live in the screens via can(). Routes absent here are
 * readable by any signed-in user.
 */
export const ROUTE_READ_PERM: Partial<Record<string, string>> = {
  // Queues process (2026-07-17 — queuing first pass; section-tab restructure
  // same day). `queue.join` + `queue.display` are the customer/lobby public
  // surfaces — deliberately unguarded.
  queues: "queues.read",
  "queues.console": "queue.serve",
  "queues.flows": "queueflows.read",
  "queues.flow": "queueflows.read",
  "queues.health": "queuehealth.read",
  "queues.dashboard": "queuehealth.read",
  "queues.boards": "queues.read",
  "queues.archive": "queues.read",
  "queues.create": "queues.write",
  // Appointments process (2026-07-26, APT-13). Gate ids are the APT_BRIDGE set
  // (see access.ts) until the canonical appointments.* catalog lands with the
  // auth wave. `appointment.manage` is the customer self-service page —
  // deliberately unguarded, like queue.join.
  appointments: "appointments.read",
  eventday: "onsite.walkup",
  /* Events section index (2026-08-06). Gated on the same read cap as the events
     list; the nine rows inside it carry their own per-surface gates. */
  "events.hub": "events.read",
  services: "services.read",
  "services.edit": "services.read",
  "services.categories": "services.read",
  "services.category.edit": "services.read",
  "services.packages": "services.read",
  "services.package.edit": "services.read",
  products: "products.read",
  "products.edit": "products.read",
  surveys: "surveys.read",
  "surveys.questions": "surveys.read",
  "surveys.edit": "surveys.read",
  "surveys.question.edit": "surveys.read",
  "messaging.flows": "flows.read",
  "messaging.flow": "flows.read",
  "messaging.delivery": "delivery.read",
  // Booking comms (APT-08) — appointment trigger templates; APT_BRIDGE cap
  // until the canonical appointments.* catalog lands (REQ-089).
  "messaging.appointments": "appointments.manage",
  inbox: "inbox.read",
  locations: "locations.read",
  "locations.edit": "locations.read",
  /* Bulk create (2026-08-07, Nick) — a CREATION surface, so it guards on
     locations.write rather than .read: a role that may look at the estate but
     not change it has no business deep-linking into the importer. */
  "locations.import": "locations.write",
  // People (2026-08-06) — merged users + bookable staff. Same read gate as the
  // two surfaces it joins, so no persona gains reach it didn't already have.
  people: "team.read",
  staff: "team.read",
  "staff.edit": "team.read",
  "staff.groups": "team.read",
  "staff.group.edit": "team.read",
  resources: "locations.read",
  "resources.edit": "locations.read",
  "resources.groups": "locations.read",
  "resources.group.edit": "locations.read",
  "settings.audit": "audit.read",
  "settings.api": "observability.read",
  "settings.security": "compliance.read",
  "settings.integ": "integrations.read",
  "settings.integ.detail": "integrations.read",
  "settings.billing": "billing.read",
  "settings.tax": "billing.read",
  orders: "billing.read",
  "settings.approvals": "settings.read",
  "settings.ai": "settings.read",
  "settings.branding": "branding.read",
  branding: "branding.read",
  // Workforce process (2026-07-16 scaffold — spec: specs/workforce-management-spec-2026-07-16.md)
  workforce: "schedules.read",
  "workforce.schedule": "schedules.read",
  "workforce.forecast": "demand.read",
  "workforce.coverage": "demand.read",
  "workforce.staffing": "staffing.read",
  "workforce.staffing.event": "staffing.read",
  "workforce.ops": "wfops.read",
  "workforce.requests": "swaps.read",
  "workforce.rules": "laborrules.read",
  "workforce.myday": "myschedule.read",
  "workforce.myday.sandbox": "myschedule.read",
  "workforce.myschedule.sandbox": "myschedule.read",
  "workforce.scheduletime.sandbox": "myschedule.read",
  "workforce.myskills": "myschedule.read",
  // Store Ops process (2026-07-16 — Maison Rivière fidelity port)
  storeops: "storeops.read",
  "storeops.floor": "storeops.read",
  "storeops.insights": "storeops.read",
  "storeops.clients": "storeops.read",
  "storeops.events": "storeops.read",
  "storeops.roster": "storeops.floorplan",
  "storeops.skills": "storeops.floorplan",
  "storeops.perf": "storeops.floorplan",
  "storeops.loyalty": "storeops.read",
  "storeops.targets": "storeops.read",
  "storeops.schedules": "storeops.floorplan",
  "storeops.staff": "storeops.read",
  "storeops.integrations": "storeops.read",
  "storeops.compliance": "storeops.read",
  "storeops.settings": "storeops.manage",
  "storeops.region": "storeops.region",
  "storeops.my.day": "mybook.read",
  "storeops.my.book": "mybook.read",
  "storeops.my.targets": "mybook.read",
  "storeops.my.schedule": "mybook.read",
  // Aperture Bank six-persona port (P1, 2026-07-17)
  "workforce.setup": "settings.read",
  "workforce.copilot": "copilot.run",
  "workforce.runs": "runs.read",
  "workforce.draft": "schedules.publish",
  "workforce.marketplace": "swaps.read",
  "workforce.reporting": "wfreports.read",
  "workforce.payroll": "payroll.read",
  "workforce.advisors": "advisors.read",
  "workforce.compliance": "compliance.read",
  "workforce.integration": "integrations.read",
  "workforce.planning": "hrplanning.read",
  "workforce.coaching": "coaching.read",
  "workforce.assists": "assists.read",
  "workforce.requisitions": "requisitions.read",
  "workforce.region": "region.read",
  "workforce.region.compare": "region.read",
  "workforce.region.redistribute": "region.read",
  "workforce.region.runs": "region.read",
  "workforce.region.scope": "region.read",
  // Sarah's Floor board (P2 port, 2026-07-20 — spec §3.11): its own route so the
  // shared workforce.ops board keeps serving the generic tenants unchanged.
  "workforce.floor": "wfops.read",
  // Calendar insertion phase 2 (2026-08-08, Greg): same floor-ops read gate.
  "floor.calendar": "wfops.read",
  /* Region layer (2026-08-10): the regional manager's own read cap — which is
     also the persona gate. A store manager holds wfops.read but not region.read,
     so the Floor board is hers and the region board is not. */
  "floor.region": "region.read",
  "workforce.myperf": "myschedule.read",
  "workforce.myrelationships": "myschedule.read",
  "ops.today": "events.read",
  "ops.walkins": "onsite.queue",
  "ops.services": "services.read",
  "ops.catalogue": "services.read",
  "workforce.services.view": "myschedule.read",
  /* The branch manager configures her team's service sign-off here (2026-08-03); advisors.read is
     the manager-scoped capability the Advisors screen already uses. */
  "workforce.coverage.staff": "advisors.read",
  // The journeys.* capability family exists specifically for this surface, so the
  // Journey builder gates on journeys.read rather than the general events read cap.
  // This is a TIGHTENING: previously the only way to open Journey was to hold the
  // whole events read surface, which the Operations Admin persona must not hold.
  // System Administrator holds every capability and is unaffected; the events
  // manager bundle carries no journeys.* cap, matching the canonical role model.
  journeys: "journeys.read",
};
